<template>
  <div class="qh-page navigation-config-page">
    <header class="config-header">
      <div>
        <span class="config-kicker">Administración</span>
        <h1>Configuración de navegación</h1>
        <p>Organiza los módulos y categorías que verá el cliente en Qhatu.</p>
      </div>
      <div class="config-summary" aria-label="Resumen">
        <div><strong>{{ ListaModulos.length }}</strong><span>Módulos</span></div>
        <div><strong>{{ clienteSummary.proyectos }}</strong><span>Proyectos</span></div>
        <div><strong>{{ clienteSummary.canales }}</strong><span>Canales</span></div>
        <div><strong>{{ ListaCategorias.length }}</strong><span>Categorías</span></div>
      </div>
    </header>

    <section class="config-guide">
      <span class="guide-title">Acciones disponibles</span>
      <div class="guide-items">
        <span><i class="bi bi-toggle-on text-success"></i> Activar</span>
        <span><i class="bi bi-toggle-off text-secondary"></i> Desactivar</span>
        <span><i class="bi bi-pencil-square text-warning"></i> Editar</span>
        <span><i class="bi bi-copy text-primary"></i> Copiar</span>
        <span><i class="bi bi-trash3 text-danger"></i> Eliminar</span>
      </div>
    </section>

    <nav class="config-tabs" aria-label="Secciones de configuración">
      <button type="button" :class="{ active: activeSection === 'modulos' }" @click="activeSection = 'modulos'">
        <i class="bi bi-grid"></i>
        <span>Módulos</span>
        <small>{{ ListaModulos.length }}</small>
      </button>
      <button type="button" :class="{ active: activeSection === 'cliente' }" @click="activeSection = 'cliente'">
        <i class="bi bi-diagram-3"></i>
        <span>Cliente</span>
        <small>{{ clienteSummary.proyectos + clienteSummary.canales }}</small>
      </button>
      <button type="button" :class="{ active: activeSection === 'categorias' }" @click="activeSection = 'categorias'">
        <i class="bi bi-bookmarks"></i>
        <span>Categorías</span>
        <small>{{ ListaCategorias.length }}</small>
      </button>
    </nav>

    <section v-show="activeSection === 'modulos'" class="config-workspace">
      <article class="config-card create-card">
        <header class="card-heading">
          <div class="heading-icon"><i class="bi bi-plus-square"></i></div>
          <div>
            <h2>Crear módulo</h2>
            <p>Clasificación principal que agrupa las opciones del cliente.</p>
          </div>
        </header>

        <form class="compact-form" @submit.prevent="GuardarModulo">
          <div class="icon-selector-section">
            <div class="field-heading">
              <div><label>Icono del módulo</label><small>Selecciona una representación visual.</small></div>
              <div class="selected-icon-preview"><i :class="selectedIcon"></i><span>{{ selectedIcon === 'ninguno' ? 'Sin seleccionar' : 'Seleccionado' }}</span></div>
            </div>
            <div class="icon-picker" role="listbox" aria-label="Iconos del módulo">
              <button v-for="icon in icons" :key="`module-${icon.idIcono}`" type="button"
                :class="{ selected: icon.nomIcono === selectedIcon }"
                :title="icon.nomIcono" @click="selectIcon(icon.nomIcono, icon.idIcono)">
                <i :class="icon.nomIcono"></i>
              </button>
            </div>
          </div>

          <div class="module-create-row">
            <div class="field-group">
              <label for="nombreModulo"><i class="bi bi-building"></i> Nombre del módulo</label>
              <input id="nombreModulo" v-model.trim="nombreModulo" type="text" class="form-control"
                placeholder="Ej. Clorox Moderno" required />
              <small>Este nombre aparecerá en el menú principal.</small>
            </div>
            <button class="primary-action" type="submit"><i class="bi bi-check2-circle"></i> Guardar módulo</button>
          </div>
        </form>
      </article>

      <article class="config-card list-card">
        <header class="card-heading compact">
          <div class="heading-icon"><i class="bi bi-list-ul"></i></div>
          <div><h2>Módulos registrados</h2><p>Edita el nombre, icono o estado desde una sola tabla.</p></div>
        </header>
        <TablaModulo />
      </article>
    </section>

    <section v-show="activeSection === 'cliente'" class="config-workspace">
      <ClienteJerarquia @summary="actualizarResumenCliente" @updated="cargarJerarquia" />
    </section>

    <section v-show="activeSection === 'categorias'" class="config-workspace">
      <article class="config-card create-card">
        <header class="card-heading">
          <div class="heading-icon"><i class="bi bi-bookmark-plus"></i></div>
          <div><h2>Crear categoría</h2><p>Opción de segundo nivel asociada a un módulo.</p></div>
        </header>

        <form class="compact-form" @submit.prevent="GuardarCategoria">
          <div class="icon-selector-section">
            <div class="field-heading">
              <div><label>Icono de la categoría</label><small>Se mostrará junto al nombre en el menú.</small></div>
              <div class="selected-icon-preview"><i :class="selectedIconCategoria"></i><span>{{ selectedIconCategoria === 'ninguno' ? 'Sin seleccionar' : 'Seleccionado' }}</span></div>
            </div>
            <div class="icon-picker" role="listbox" aria-label="Iconos de categoría">
              <button v-for="icon in icons" :key="`category-${icon.idIcono}`" type="button"
                :class="{ selected: icon.nomIcono === selectedIconCategoria }"
                :title="icon.nomIcono" @click="selectIconCategoria(icon.nomIcono, icon.idIcono)">
                <i :class="icon.nomIcono"></i>
              </button>
            </div>
          </div>

          <div class="category-form-grid">
            <div class="field-group">
              <label for="nombreCategoria"><i class="bi bi-tag"></i> Nombre de la categoría</label>
              <input id="nombreCategoria" v-model.trim="nombreCategoria" type="text" class="form-control" placeholder="Ej. Cobertura de servicio" required />
            </div>
            <div class="field-group">
              <label for="descripcionLinkBI"><i class="bi bi-link-45deg"></i> Link Power BI</label>
              <input id="descripcionLinkBI" v-model.trim="descripcionLinkBI" type="url" class="form-control" placeholder="https://app.powerbi.com/..." required />
            </div>
            <div class="field-group autocomplete-field">
              <label for="busquedaModulo"><i class="bi bi-grid"></i> Módulo asociado</label>
              <input id="busquedaModulo" v-model="busquedaModulo" type="text" class="form-control"
                placeholder="Buscar módulo..." @input="filtrarModulos" @focus="filtrarModulos" @blur="ocultarListaModulos" required />
              <ul v-if="mostrarListaModulos && modulosFiltrados.length" class="autocomplete-list">
                <li v-for="mod in modulosFiltrados" :key="mod.idModulo" @mousedown.prevent="seleccionarModulo(mod)">
                  <i :class="mod.nomIcono || 'bi bi-grid'"></i><span>{{ mod.nomModulo }}</span>
                </li>
              </ul>
            </div>
            <div class="field-group autocomplete-field">
              <label for="selectProyecto"><i class="bi bi-folder2-open"></i> Proyecto asociado</label>
              <div v-if="proyectos.length > 4" class="position-relative">
                <div class="input-group">
                  <input
                    id="selectProyecto"
                    v-model="busquedaProyectoCat"
                    type="text"
                    class="form-control"
                    placeholder="Buscar proyecto..."
                    @focus="mostrarListaProyectosCat = true"
                    @input="mostrarListaProyectosCat = true"
                    @blur="ocultarListaProyectosCat"
                  />
                  <button v-if="idProyectoSeleccionado || busquedaProyectoCat" type="button" class="btn btn-outline-secondary" @click="limpiarProyectoCat">
                    <i class="bi bi-x-lg"></i>
                  </button>
                </div>
                <ul v-if="mostrarListaProyectosCat && proyectosCatFiltrados.length" class="autocomplete-list">
                  <li v-for="p in proyectosCatFiltrados" :key="p.idProyecto" @mousedown.prevent="seleccionarProyectoCat(p)">
                    <i class="bi bi-folder2 text-primary"></i><span>{{ p.orden }} · {{ p.descripcion }}</span>
                  </li>
                </ul>
              </div>
              <select v-else id="selectProyecto" v-model="idProyectoSeleccionado" class="form-select" @change="idSubPry = ''; busquedaCanalCat = ''">
                <option value="">Seleccionar proyecto...</option>
                <option v-for="p in proyectos" :key="p.idProyecto" :value="p.idProyecto">
                  {{ p.orden }} · {{ p.descripcion }}
                </option>
              </select>
            </div>
            <div class="field-group autocomplete-field">
              <label for="selectCanal"><i class="bi bi-signpost-split"></i> Canal asociado</label>
              <div v-if="canalesFiltradosPorProyecto.length > 4" class="position-relative">
                <div class="input-group">
                  <input
                    id="selectCanal"
                    v-model="busquedaCanalCat"
                    type="text"
                    class="form-control"
                    placeholder="Buscar canal..."
                    :disabled="!idProyectoSeleccionado"
                    @focus="mostrarListaCanalesCat = true"
                    @input="mostrarListaCanalesCat = true"
                    @blur="ocultarListaCanalesCat"
                  />
                  <button v-if="idSubPry || busquedaCanalCat" type="button" class="btn btn-outline-secondary" @click="limpiarCanalCat">
                    <i class="bi bi-x-lg"></i>
                  </button>
                </div>
                <ul v-if="mostrarListaCanalesCat && canalesCatFiltrados.length" class="autocomplete-list">
                  <li v-for="c in canalesCatFiltrados" :key="c.id" @mousedown.prevent="seleccionarCanalCat(c)">
                    <i class="bi bi-signpost-split text-primary"></i><span>{{ c.orden }} · {{ c.descripcion }}</span>
                  </li>
                </ul>
              </div>
              <select v-else id="selectCanal" v-model="idSubPry" class="form-select" :disabled="!idProyectoSeleccionado || !canalesFiltradosPorProyecto.length">
                <option value="">{{ !idProyectoSeleccionado ? 'Primero selecciona un proyecto...' : (canalesFiltradosPorProyecto.length ? 'Seleccionar canal...' : 'Sin canales para este proyecto') }}</option>
                <option v-for="c in canalesFiltradosPorProyecto" :key="c.id" :value="c.id">
                  {{ c.orden }} · {{ c.descripcion }}
                </option>
              </select>
            </div>
            <div class="field-group">
              <label for="catOrden"><i class="bi bi-sort-numeric-down"></i> Prioridad de orden</label>
              <input id="catOrden" v-model.trim="catOrden" type="number" min="0" class="form-control" placeholder="Ej. 1" required />
            </div>
            <div class="field-group wide-detail">
              <label for="catDetalle"><i class="bi bi-card-text"></i> Detalle de la categoría</label>
              <input id="catDetalle" v-model.trim="catDetalle" type="text" class="form-control" placeholder="Descripción breve para administración" required />
            </div>
            <div class="form-action-cell">
              <button class="primary-action full" type="submit"><i class="bi bi-check2-circle"></i> Guardar categoría</button>
            </div>
          </div>
        </form>
      </article>

      <article class="config-card list-card">
        <header class="card-heading compact">
          <div class="heading-icon"><i class="bi bi-list-check"></i></div>
          <div><h2>Categorías registradas</h2><p>Administra contenido, orden y acceso a Power BI.</p></div>
        </header>
        <TablaCategoria />
      </article>
    </section>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Swal from "sweetalert";
import Swal2 from "sweetalert2";
import TablaCategoria from "@/components/Configuracion/Tablas/tablaCategoria.vue";
import TablaModulo from "@/components/Configuracion/Tablas/tablaModulo.vue";
import ClienteJerarquia from "@/components/Configuracion/Cliente/ClienteJerarquia.vue";
export default {
  name: "PowerbyConfiguracion",
  components: { TablaModulo, TablaCategoria, ClienteJerarquia },

  data() {
    return {
      activeSection: "modulos",
      clienteSummary: { proyectos: 0, canales: 0 },
      nombreModulo: "",
      selectedIcon: "ninguno",
      selectedIconCategoria: "ninguno",

      icons: [],
      idIcono: "5",
      idIconoCategoria: "5",
      idUsuario: "",

      // PARA EL BUSCADOR DE MÓDULOS
      busquedaModulo: "",
      mostrarListaModulos: false,
      modulosFiltrados: [],

      proyectos: [],
      canales: [],
      idProyectoSeleccionado: "",
      idSubPry: "",
      busquedaProyectoCat: "",
      mostrarListaProyectosCat: false,
      busquedaCanalCat: "",
      mostrarListaCanalesCat: false,

      idModulo: "",
      nombreCategoria: "",
      descripcionLinkBI: "",
      perfilNombre: "",
      cambiarPermisos: true,
      catOrden: "",
      catDetalle: "",
    };
  },

  mounted() {
    this.idUsuario = localStorage.getItem("userId");
    this.axios.get(`BIClientes/ListadoIconos`).then((r) => {
      console.log(r.data);
      this.icons = r.data;
    });
    this.cargarJerarquia();
  },

  created() {
    this.ListarCategorias();
  },

  computed: {
    ...mapGetters("acciones", ["ListaModulos", "ListaCategorias"]),
    canalesFiltradosPorProyecto() {
      if (!this.idProyectoSeleccionado) return [];
      return this.canales.filter(
        c => String(c.idProyecto) === String(this.idProyectoSeleccionado)
      );
    },
    proyectosCatFiltrados() {
      const q = (this.busquedaProyectoCat || "").toLowerCase().trim();
      return this.proyectos.filter(
        p => !q || `${p.orden} ${p.descripcion}`.toLowerCase().includes(q)
      );
    },
    canalesCatFiltrados() {
      const q = (this.busquedaCanalCat || "").toLowerCase().trim();
      return this.canalesFiltradosPorProyecto.filter(
        c => !q || `${c.orden} ${c.descripcion}`.toLowerCase().includes(q)
      );
    },
  },

  watch: {
    activeSection(newSection) {
      if (newSection === "categorias" || newSection === "cliente") {
        this.cargarJerarquia();
      }
    },
  },

  methods: {
    ...mapActions("acciones", ["ListarModulo", "ListarCategorias"]),

    cargarJerarquia() {
      Promise.all([
        this.axios.get("BIClientes/ListarProyectos"),
        this.axios.get("BIClientes/ListarSubProyectos"),
      ])
        .then(([rp, rc]) => {
          const rawP = Array.isArray(rp.data) ? rp.data : (rp.data?.datos || []);
          this.proyectos = rawP.map(p => ({
            idProyecto: p.id || p.idPry || p.idProyecto,
            orden: p.nrOrden ?? p.orden ?? "",
            descripcion: p.pryDescripcion  || "",
          }));

          const rawC = Array.isArray(rc.data) ? rc.data : (rc.data?.datos || []);
          this.canales = rawC.map(c => ({
            id: c.id || c.idSubPry || c.idCanal,
            idSubPry: c.id || c.idSubPry || c.idCanal,
            idCanal: c.id || c.idSubPry || c.idCanal,
            orden: c.nrOrden ?? c.orden ?? "",
            idProyecto: c.idProyecto,
            descripcion: c.subPryDescripcion || "",
          }));
        })
        .catch(() => {
          this.proyectos = [];
          this.canales = [];
        });
    },

    actualizarResumenCliente(resumen) {
      this.clienteSummary = resumen || { proyectos: 0, canales: 0 };
    },

    GuardarModulo() {
      const from = {
        idUsuario: this.idUsuario,
        idIcono: this.idIcono,
        nomModulo: this.nombreModulo,
      };
      console.log(from);
      this.axios.post(`BIClientes/RegistrarModulo`, from).then((r) => {
        console.log(r.data);

        if (r.data) {
          this.ListarModulo();
          Swal(`Se registró el módulo ${this.nombreModulo}`, "", "success");
          this.idIcono = "5"
          this.nombreModulo = "";
          this.selectedIcon = "";
        }
      });
    },

    selectIcon(iconClass, id) {
      this.selectedIcon = iconClass;
      this.idIcono = id;
    },

    selectIconCategoria(iconClass, id) {
      this.selectedIconCategoria = iconClass;
      this.idIconoCategoria = id;
    },

    GuardarCategoria() {
      // Extraer Group ID y Report ID del link Power BI
      const regex = /groups\/([a-z0-9-]+)\/reports\/([a-z0-9-]+)/;
      const match = this.descripcionLinkBI.match(regex);

      // Definir groupId y reportId
      let grupoid = "";
      let reporteid = "";

      if (match) {
        grupoid = match[2]; // Extraer el Group ID
        reporteid = match[1]; // Extraer el Report ID
        console.log("Group ID:", grupoid);
        console.log("Report ID:", reporteid);
      } else {
        console.log("El link de Power BI no es válido.");
      }

      const from = {
        idUsuario: this.idUsuario,
        idIcono: this.idIconoCategoria,
        idModulo: this.idModulo,
        idProyecto: this.idProyectoSeleccionado,
        idPry: this.idProyectoSeleccionado,
        idSubPry: this.idSubPry,
        idCanal: this.idSubPry,
        catNombre: this.nombreCategoria,
        linkBI: this.descripcionLinkBI,
        catOrden: this.catOrden,
        catDetalle: this.catDetalle,
        grupoid: grupoid, // Ahora se asigna el valor correcto
        reporteid: reporteid, // Ahora se asigna el valor correcto
      };

      console.log(from);
      this.axios.post(`BIClientes/RegistrarCategoria`, from).then((r) => {
        console.log(r.data);
        if (r.data) {
          this.ListarModulo();
          Swal(
            `Se registró la categoría ${this.nombreCategoria}`,
            "",
            "success"
          );
          this.nombreCategoria = "";
          this.selectedIcon = "";
          this.descripcionLinkBI = "";
          this.catOrden = "";
          this.catDetalle = "";
          this.idProyectoSeleccionado = "";
          this.idSubPry = "";
          this.busquedaProyectoCat = "";
          this.busquedaCanalCat = "";
          this.ListarCategorias();
        }

        if (!r.data) {
          Swal2.fire({
            icon: "error",
            title: `LA CATEGORIA ${this.nombreCategoria} YA SE ENCUENTRA ASOCIADA A ESTE MODULO`,
            text: "",
            footer: "",
          });
        }
      });
    },
    filtrarModulos() {
      const texto = this.busquedaModulo.trim().toLowerCase();

      // Mostrar lista si hay texto
      this.mostrarListaModulos = texto.length > 0;

      // Filtrar módulos desde Vuex
      this.modulosFiltrados = this.ListaModulos.filter((mod) =>
        mod.nomModulo.toLowerCase().includes(texto)
      );
    },

    seleccionarModulo(mod) {
      this.busquedaModulo = mod.nomModulo;
      this.idModulo = mod.idModulo; // ← lo que luego usas para registrar categoría
      this.mostrarListaModulos = false;
    },

    ocultarListaModulos() {
      setTimeout(() => {
        this.mostrarListaModulos = false;
      }, 200);
    },

    seleccionarProyectoCat(p) {
      this.idProyectoSeleccionado = p.idProyecto;
      this.busquedaProyectoCat = `${p.orden} · ${p.descripcion}`;
      this.mostrarListaProyectosCat = false;
      this.limpiarCanalCat();
    },
    ocultarListaProyectosCat() {
      setTimeout(() => { this.mostrarListaProyectosCat = false; }, 200);
    },
    limpiarProyectoCat() {
      this.idProyectoSeleccionado = "";
      this.busquedaProyectoCat = "";
      this.limpiarCanalCat();
    },
    seleccionarCanalCat(c) {
      this.idSubPry = c.id;
      this.busquedaCanalCat = `${c.orden} · ${c.descripcion}`;
      this.mostrarListaCanalesCat = false;
    },
    ocultarListaCanalesCat() {
      setTimeout(() => { this.mostrarListaCanalesCat = false; }, 200);
    },
    limpiarCanalCat() {
      this.idSubPry = "";
      this.busquedaCanalCat = "";
    },
  },
};
</script>

<style scoped>
.navigation-config-page { display:grid; gap:.85rem; }
.config-header { display:flex; align-items:flex-end; justify-content:space-between; gap:1rem; padding:.25rem 0 .45rem; }
.config-kicker { color:var(--qhatu-primary); font-size:.7rem; font-weight:800; letter-spacing:.11em; text-transform:uppercase; }
.config-header h1 { margin:.2rem 0 .25rem; color:var(--qhatu-text); font-size:clamp(1.45rem,2vw,2rem); font-weight:780; }
.config-header p { margin:0; color:var(--qhatu-muted); }
.config-summary { display:flex; gap:.5rem; }
.config-summary div { min-width:88px; padding:.6rem .8rem; border:1px solid var(--qhatu-border); border-radius:12px; background:#fff; text-align:center; }
.config-summary strong { display:block; color:var(--qhatu-primary); font-size:1.15rem; line-height:1.1; }
.config-summary span { color:var(--qhatu-muted); font-size:.72rem; }
.config-guide { display:flex; align-items:center; gap:1rem; padding:.65rem .85rem; border:1px solid var(--qhatu-border); border-radius:12px; background:#fff; }
.guide-title { font-size:.75rem; font-weight:750; color:var(--qhatu-text); white-space:nowrap; }
.guide-items { display:flex; align-items:center; flex-wrap:wrap; gap:.45rem .9rem; }
.guide-items span { display:inline-flex; align-items:center; gap:.35rem; color:var(--qhatu-muted); font-size:.76rem; }
.config-tabs { display:flex; gap:.5rem; padding:.35rem; width:max-content; max-width:100%; border:1px solid var(--qhatu-border); border-radius:14px; background:#fff; }
.config-tabs button { min-height:42px; display:flex; align-items:center; gap:.5rem; padding:.55rem .9rem; border:0; border-radius:10px; background:transparent; color:var(--qhatu-muted); font-weight:700; }
.config-tabs button.active { background:var(--qhatu-primary); color:#fff; box-shadow:0 7px 16px rgba(8,123,184,.2); }
.config-tabs small { min-width:24px; padding:.15rem .4rem; border-radius:999px; background:rgba(127,145,165,.14); font-size:.68rem; }
.config-tabs button.active small { background:rgba(255,255,255,.2); }
.config-workspace { display:grid; gap:.8rem; }
.config-card { border:1px solid var(--qhatu-border); border-radius:15px; background:#fff; box-shadow:0 8px 24px rgba(22,48,76,.05); overflow:visible; }
.card-heading { display:flex; align-items:center; gap:.75rem; padding:.85rem 1rem; border-bottom:1px solid var(--qhatu-border); }
.card-heading.compact { padding:.7rem .9rem; }
.heading-icon { width:40px; height:40px; flex:0 0 40px; display:grid; place-items:center; border-radius:11px; background:var(--qhatu-primary-soft); color:var(--qhatu-primary); }
.card-heading h2 { margin:0; color:var(--qhatu-text); font-size:1rem; font-weight:760; }
.card-heading p { margin:.15rem 0 0; color:var(--qhatu-muted); font-size:.78rem; }
.compact-form { display:grid; gap:.85rem; padding:.85rem 1rem 1rem; }
.icon-selector-section { display:grid; gap:.55rem; }
.field-heading { display:flex; align-items:center; justify-content:space-between; gap:1rem; }
.field-heading label, .field-group label { display:block; margin-bottom:.35rem; color:#31445b; font-size:.78rem; font-weight:750; }
.field-heading small, .field-group small { display:block; color:var(--qhatu-muted); font-size:.72rem; }
.selected-icon-preview { display:flex; align-items:center; gap:.45rem; padding:.38rem .6rem; border-radius:10px; background:#f4f8fc; color:var(--qhatu-primary); font-size:.72rem; font-weight:700; }
.selected-icon-preview i { font-size:1rem; }
.icon-picker { display:grid; grid-template-columns:repeat(auto-fill,minmax(42px,1fr)); gap:.38rem; max-height:104px; overflow:auto; padding:.4rem; border:1px solid var(--qhatu-border); border-radius:12px; background:#f8fafc; }
.icon-picker button { height:38px; border:1px solid #dce5ef; border-radius:9px; background:#fff; color:#18324d; }
.icon-picker button:hover { border-color:#86bde1; color:var(--qhatu-primary); }
.icon-picker button.selected { border-color:var(--qhatu-primary); background:var(--qhatu-primary-soft); color:var(--qhatu-primary); box-shadow:0 0 0 2px rgba(8,123,184,.1); }
.module-create-row { display:grid; grid-template-columns:minmax(260px,430px) auto; gap:.7rem; align-items:start; }
.module-create-row > .primary-action { margin-top:1.55rem; }
.category-form-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:.75rem; align-items:flex-end; }
.field-group { min-width:0; position:relative; }
.field-group label i { margin-right:.35rem; color:#71849a; }
.field-group .form-control { min-height:44px; border-radius:10px; }
.wide-detail { grid-column:span 2; }
.form-action-cell { display:flex; align-items:flex-end; }
.primary-action { min-height:44px; display:inline-flex; align-items:center; justify-content:center; gap:.45rem; padding:.65rem 1rem; border:0; border-radius:10px; background:var(--qhatu-primary); color:#fff; font-weight:750; white-space:nowrap; }
.primary-action:hover { background:var(--qhatu-primary-dark); }
.primary-action.full { width:100%; }
.autocomplete-list { position:absolute; z-index:30; top:calc(100% + 4px); left:0; right:0; max-height:220px; overflow:auto; margin:0; padding:.35rem; list-style:none; border:1px solid var(--qhatu-border); border-radius:12px; background:#fff; box-shadow:0 16px 38px rgba(22,48,76,.16); }
.autocomplete-list li { display:flex; align-items:center; gap:.55rem; padding:.6rem .7rem; border-radius:9px; color:#3b4f65; cursor:pointer; font-size:.82rem; }
.autocomplete-list li:hover { background:var(--qhatu-primary-soft); color:var(--qhatu-primary); }
.list-card { overflow:hidden; }
@media(max-width:900px){ .category-form-grid{grid-template-columns:repeat(2,minmax(0,1fr));}.wide-detail{grid-column:span 1}.form-action-cell{grid-column:span 2}.config-header{align-items:flex-start}.config-summary{display:none} }
@media(max-width:575.98px){ .navigation-config-page{gap:.65rem}.config-guide{align-items:flex-start;flex-direction:column;gap:.45rem}.config-tabs{width:100%}.config-tabs button{flex:1;justify-content:center}.module-create-row,.category-form-grid{grid-template-columns:1fr}.module-create-row>.primary-action{margin-top:0}.wide-detail,.form-action-cell{grid-column:span 1}.primary-action{width:100%}.field-heading{align-items:flex-start;flex-direction:column}.icon-picker{grid-template-columns:repeat(6,1fr)} }
</style>
